import { gradingIdSchema } from "@repo/app-types/entities/teammatePerformanceSummary";
import { createContext, useContext } from "react";
import { z } from "zod";

/** URL query param carrying the research thread ID (deep links, share links). */
export const RESEARCH_THREAD_ID_PARAM = "research_thread_id";

/** Title shown until a thread is given an explicit title. */
export const FALLBACK_THREAD_TITLE = "New chat";

/** Schema for a single share entry in thread metadata */
export const threadShareEntrySchema = z.object({
  with: z.enum(["company"]),
  at: z.string(), // ISO date string
});

export type ThreadShareEntry = z.infer<typeof threadShareEntrySchema>;

const baseThreadMetadata = z.object({
  shared: z.array(threadShareEntrySchema).optional(),
  archived: z.boolean().optional(),
});

/**
 * Threads are company-scoped and carry no scope ids of their own; grounding
 * comes from the page context sent with each message. The id is validated,
 * not just typed as a string, so a stored payload carrying something that
 * isn't a real id does not survive a read.
 */
export const copilotThreadMetadataSchema = baseThreadMetadata.extend({
  /** The call whose transcript the chat was started from, which reopens it. */
  gradingId: gradingIdSchema.optional(),
});

export type CopilotThreadMetadata = z.infer<typeof copilotThreadMetadataSchema>;

/** Check if a thread is shared with the company */
export function checkIsThreadSharedWithCompany(
  metadata: CopilotThreadMetadata | undefined,
): boolean {
  return metadata?.shared?.some((entry) => entry.with === "company") ?? false;
}

/** A user message and a reply written by the app, shown without an agent run. */
export interface CannedExchange {
  userText: string;
  replyText: string;
}

/**
 * One Ask Alpharun chat: the selected thread and the composer's draft. The
 * main chat and a call transcript's chat are separate instances.
 */
export interface CopilotChat {
  /** The Mastra resourceId for the current authenticated user. */
  resourceId: string;
  /** The client-side identifier of the currently selected thread. */
  localThreadId: string;
  /** The Mastra threadId for the currently selected thread. `undefined` if it's a new/unsaved thread. */
  threadId: string | undefined;
  /**
   * The selected thread's title as it was already known when it was selected.
   * Lets the header name the thread straight away, rather than blanking to the
   * fallback while the thread's own fetch is in flight.
   */
  threadTitle: string | undefined;
  /** The resourceId of the thread owner. Can be used to determine if the thread is owned by the authenticated user. */
  threadResourceId: string;
  /** The thread metadata indicating the context (playbook or teammate agent) of the current thread. */
  threadMetadata: CopilotThreadMetadata;
  /** Whether we attempted to load a thread that was not found or not accessible. */
  isThreadNotFound: boolean;

  /** Select the given Mastra thread. */
  onThreadSelect: (thread: {
    id: string;
    resourceId: string;
    title: string | undefined;
    metadata: CopilotThreadMetadata;
  }) => void;
  /**
   * Select a new thread, optionally with an initial message to automatically
   * send, or a canned exchange to add, into the new thread.
   */
  onNewThreadSelect: (options?: {
    initialUserMessageText?: string;
    initialExchange?: CannedExchange;
  }) => void;
  /**
   * Send a message into the selected thread, or into a new thread when the
   * selected one can't take it: another user's shared thread, or one that
   * failed to load.
   */
  onSendMessage: (text: string) => void;
  /**
   * Add a canned exchange to the selected thread without running the agent. It
   * is saved to the thread with the user's next message. Falls back to a new
   * thread as `onSendMessage` does.
   */
  onAppendExchange: (exchange: CannedExchange) => void;
  /** Whether the agent is replying in the selected thread or loading its history. */
  isCopilotBusy: boolean;
  /**
   * Bumped every time the user starts a new chat, which is the only thing that
   * moves focus into the composer on its own. A counter rather than a flag so
   * the chat view can tell a fresh request from a re-run of the same one —
   * including StrictMode's double-invoked effects.
   */
  composerFocusRequestId: number;

  /**
   * The composer's unsent text. Held here rather than in the composer so it
   * survives moving between the panel and the modal, which never render at the
   * same time.
   */
  composerDraft: string;
  onComposerDraftChange: (newComposerDraft: string) => void;
}

/** The main Ask Alpharun chat, with the surfaces that show it. */
export interface CopilotProps extends CopilotChat {
  /**
   * Whether the docked side panel is on screen. Always false on viewports too
   * narrow to dock it, where the expanded modal hosts the chat instead.
   */
  isPanelVisible: boolean;
  /** Whether the chat is on screen at all, on any surface. */
  isChatVisible: boolean;
  /** Show the chat on whichever surface the viewport supports. */
  onOpenChat: () => void;
  /** Hide whichever chat surface is showing. */
  onCloseChat: () => void;

  /** Whether the expanded (near-fullscreen) copilot modal is open. */
  isModalOpen: boolean;
  onModalOpenChange: (newIsModalOpen: boolean) => void;

  /**
   * Registers work that must finish before a message is sent, and returns the
   * function that unregisters it. An editor whose edits autosave registers its
   * flush here, so the agent's tools read the draft the user is looking at
   * rather than the one the debounce has not written yet.
   */
  registerBeforeSend: (beforeSend: () => Promise<unknown>) => () => void;
}

export const CopilotContext = createContext<CopilotProps | undefined>(
  undefined,
);

export function useCopilotProps(): CopilotProps | undefined {
  return useContext(CopilotContext);
}
